@if (!event.questionnaireReleased) {
  <mat-error class="dt-error" i18n
    >Der Fragebogen ist nicht veröffentlicht. Teilnehmende können ihn weder einsehen noch ausfüllen. Abgaben sind nicht möglich, bis der
    Fragebogen veröffentlicht wurde.</mat-error
  >
}
@switch (event.submissionStatus) {
  @case ('open') {
    <div class="status-block submissions-open">
      <mat-icon>alarm_on</mat-icon>
      @if (event.endDate) {
        <ng-container i18n>Die Abgabe ist freigeschaltet bis zum {{ event.endDate | date: 'medium' }}.</ng-container>
      }
      @if (!event.endDate) {
        <ng-container i18n>Die Abgabe ist ohne Frist freigeschaltet.</ng-container>
      }
    </div>
  }
  @case ('upcoming') {
    <div class="status-block">
      <mat-icon>acute</mat-icon>
      <ng-container i18n>Die Abgabe wird am {{ event.startDate | date: 'medium' }} freigeschaltet.</ng-container>
    </div>
  }
  @case ('expired') {
    <div class="status-block">
      <mat-icon>lock_clock</mat-icon>
      <ng-container i18n>Der Abgabezeitraum ist abgelaufen.</ng-container>
    </div>
  }
}

<section>
  <h4 i18n>Eckdaten</h4>
  <table>
    <tr>
      <td i18n>Name der Veranstaltung</td>
      <td>{{ event.name }}</td>
    </tr>
    <tr>
      <td i18n>Registrierungs-Code</td>
      <td>{{ event.code || '—' }}</td>
    </tr>
    <tr>
      <td i18n>Start Abgabefrist</td>
      <td>
        @if (event.startDate) {
          {{ event.startDate | date: 'medium' }}
        } @else {
          <ng-container i18n>Unbefristet</ng-container>
        }
      </td>
    </tr>
    <tr>
      <td i18n>Ende Abgabefrist</td>
      <td>
        @if (event.endDate) {
          {{ event.endDate | date: 'medium' }}
        } @else {
          <ng-container i18n>Unbefristet</ng-container>
        }
      </td>
    </tr>
  </table>
</section>

<section>
  <h4 i18n>Berechtigungen</h4>
  <p class="mat-caption" i18n
    >Sowohl der Besitzer als auch alle Editoren können Änderungen an der Veranstaltung vornehmen. Nur der Besitzer kann Berechtigungen
    verändern und die Veranstaltung löschen.</p
  >
  <table class="permissions-table">
    <tr>
      <td i18n>Besitzer</td>
      <td>
        <div class="owner-cell">
          <mat-chip-set>
            <mat-chip>
              {{ event.owner.email }}
              @if (eventManagementService.isEventOwner(event)) {
                <ng-container i18n>(Du)</ng-container>
              }
            </mat-chip>
          </mat-chip-set>
          @if (eventManagementService.isEventOwner(event)) {
            <button mat-button (click)="transferOwnership()" i18n>Besitz übertragen</button>
          }
        </div>
      </td>
    </tr>
    <tr>
      <td i18n>Editoren</td>
      <td>
        @if (eventManagementService.isEventOwner(event)) {
          <form [formGroup]="form" (ngSubmit)="saveEditors()" class="form">
            <mat-form-field>
              <mat-label i18n>E-Mail-Adressen der Editoren</mat-label>
              <mat-chip-grid #editorList formControlName="editors">
                @for (editorEmail of form.value.editors; track editorEmail) {
                  <mat-chip-row (removed)="removeEditor(editorEmail)" [class.invalid]="!(editorEmail | isValidEmail)">
                    {{ editorEmail }}
                    <button [disabled]="!eventManagementService.isEventOwner(event)" matChipRemove><mat-icon>cancel</mat-icon></button>
                  </mat-chip-row>
                }
                <input
                  placeholder="Editor hinzufügen"
                  i18n-placeholder
                  [matChipInputFor]="editorList"
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  [matChipInputAddOnBlur]="true"
                  (matChipInputTokenEnd)="addEditor($event)"
                  [disabled]="!eventManagementService.isEventOwner(event)"
                />
              </mat-chip-grid>

              <button mat-icon-button [dtOverlayProgress]="form.disabled" [disabled]="form.pristine" class="save-button" matSuffix
                ><mat-icon>save</mat-icon></button
              >

              @if (form.controls.editors.errors?.emailFormat) {
                <mat-error i18n>Eine der Eingaben ist keine gültige E-Mail-Adresse</mat-error>
              }
              @if (form.controls.editors.errors?.emailNotFound) {
                <mat-error i18n>Eine der E-Mail-Adressen gehört nicht zu einem Nutzeraccount</mat-error>
              }
              @if (form.controls.editors.errors?.unknownError) {
                <mat-error i18n>Unbekannter Fehler beim Speichern</mat-error>
              }
            </mat-form-field>
          </form>
        } @else {
          <mat-chip-set>
            @for (editorEmail of form.value.editors; track editorEmail) {
              <mat-chip>
                {{ editorEmail }}
                @if (eventManagementService.isMyEmail(editorEmail)) {
                  <ng-container i18n>(Du)</ng-container>
                }
              </mat-chip>
            }
          </mat-chip-set>
        }
      </td>
    </tr>
    <tr>
      <td i18n>Betrachter</td>
      <td>
        @if (eventManagementService.isEventOwner(event)) {
          <form [formGroup]="form" (ngSubmit)="saveViewers()" class="editors-form">
            <mat-form-field>
              <mat-label i18n>E-Mail-Adressen der Betrachter</mat-label>
              <mat-chip-grid #viewerList formControlName="viewers">
                @for (viewerEmail of form.value.viewers; track viewerEmail) {
                  <mat-chip-row (removed)="removeViewer(viewerEmail)" [class.invalid]="!(viewerEmail | isValidEmail)">
                    {{ viewerEmail }}
                    <button [disabled]="!eventManagementService.isEventOwner(event)" matChipRemove><mat-icon>cancel</mat-icon></button>
                  </mat-chip-row>
                }
                <input
                  placeholder="Betrachter hinzufügen"
                  i18n-placeholder
                  [matChipInputFor]="viewerList"
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  [matChipInputAddOnBlur]="true"
                  (matChipInputTokenEnd)="addViewer($event)"
                  [disabled]="!eventManagementService.isEventOwner(event)"
                />
              </mat-chip-grid>

              <button mat-icon-button [dtOverlayProgress]="form.disabled" [disabled]="form.pristine" class="save-button" matSuffix
                ><mat-icon>save</mat-icon></button
              >

              @if (form.controls.viewers.errors?.emailFormat) {
                <mat-error i18n>Eine der Eingaben ist keine gültige E-Mail-Adresse</mat-error>
              }
              @if (form.controls.viewers.errors?.emailNotFound) {
                <mat-error i18n>Eine der E-Mail-Adressen gehört nicht zu einem Nutzeraccount</mat-error>
              }
              @if (form.controls.viewers.errors?.unknownError) {
                <mat-error i18n>Unbekannter Fehler beim Speichern</mat-error>
              }
            </mat-form-field>
          </form>
        } @else {
          <mat-chip-set>
            @for (viewerEmail of form.value.viewers; track viewerEmail) {
              <mat-chip>
                {{ viewerEmail }}
                @if (eventManagementService.isMyEmail(viewerEmail)) {
                  <ng-container i18n>(Du)</ng-container>
                }
              </mat-chip>
            }
          </mat-chip-set>
        }
      </td>
    </tr>
  </table>
</section>

@if (eventManagementService.canEditEvent(event)) {
  <section>
    <h4 i18n>Aktionen</h4>
    <div class="button-row">
      <button mat-stroked-button (click)="duplicateEvent()" i18n>Veranstaltung duplizieren</button>
      @if (eventManagementService.isEventOwner(event)) {
        <button mat-stroked-button color="warn" (click)="deleteEvent()" i18n>Veranstaltung löschen</button>
      }
    </div>
  </section>
}
